import { RemoveCircle } from '@mui/icons-material';
import type { DataGridProProps as DataGridProps, GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRemoveFromRole } from '~/api/roles';
import { ActionButton } from '~/components/button';
import { SeparatedDataGrid } from '~/components/data-grid';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { EmptyState } from '~/components/empty-state';
import { AuthorizedLink } from '~/components/links';
import { ListCheckbox } from '~/components/list-row';
import { mergeGridActionProps, type GridActionCellProps } from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import AddUsersIllustration from '~/images/illustrations/add-users.svg';
import type { RoleUserList__Role, RoleUserList__User } from '../RoleUserList.generated';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={AddUsersIllustration}
      description={t('pages.roles.users.empty_state.description')}
      header={t('pages.roles.users.empty_state.header')}
    />
  );
};

interface RemoveButtonProps extends GridActionCellProps {
  networkRole: RoleUserList__Role;
  user: RoleUserList__User;
}

const RemoveButton = ({ networkRole, user, ...cellProps }: RemoveButtonProps) => {
  const { t } = useTranslation();
  const [removeUser, { called, reset }] = useRemoveFromRole({
    variables: { roleId: networkRole.id, userIds: user.id },
  });

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const onClick = async () => {
    if (!(await confirmDelete())) return;
    await removeUser();
    reset();
  };

  return (
    <>
      <ActionButton
        color="error"
        disabled={called}
        Icon={RemoveCircle}
        {...mergeGridActionProps({ onClick }, cellProps)}
        title={t('common.actions.remove')}
      />

      <ConfirmDialog
        {...confirmDeleteProps}
        cancel={t('common.buttons.cancel')}
        confirm={t('pages.roles.dialogs.remove_user.confirm')}
        deleteConfirm
        prompt={t('pages.roles.dialogs.remove_user.prompt', {
          user_name: user.name,
          role_name: networkRole.name,
        })}
        title={t('pages.roles.dialogs.remove_user.title')}
      />
    </>
  );
};

const useColumns = (role: RoleUserList__Role): GridColDef<RoleUserList__User>[] => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        field: 'name',
        headerName: t('common.labels.name'),
        flex: 0.5,
        renderCell: ({ row }) => <AuthorizedLink authorized={row.canUpdate.value} entity={row} />,
      },
      {
        field: 'email',
        flex: 1,
        headerName: t('pages.roles.users.columns.email'),
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: ({ row }) => [<RemoveButton key={0} networkRole={role} user={row} />],
      },
    ],
    [role, t],
  );
};

export interface UserTableProps extends Omit<DataGridProps<RoleUserList__User>, 'columns'> {
  loading?: boolean;
  role: RoleUserList__Role;
}

export const UserTable = ({ loading, role, ...props }: UserTableProps) => {
  const columns = useColumns(role);

  return (
    <SeparatedDataGrid
      columns={columns}
      loading={loading}
      slots={{
        baseCheckbox: ListCheckbox,
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      {...props}
    />
  );
};
